import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ChevronLeft, Coffee, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PageMeta } from "@/components/PageMeta";
import { toast } from "sonner";
import installVideo from "@/assets/install-app.mp4.asset.json";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type Platform = "ios" | "android" | "other";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (/iPhone|iPod/.test(ua) || isIPad) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)").matches;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mql || iosStandalone);
}

export default function Help() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [platform] = useState<Platform>(() => detectPlatform());
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const installedHandler = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      toast(t("help.installUnavailable"));
      return;
    }
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setDeferredPrompt(null);
    } catch {
      toast(t("help.installUnavailable"));
    }
  };

  const canPrompt = !!deferredPrompt;
  const showFallback = !installed && (platform === "ios" || !canPrompt);

  const sections = [
    { id: "leagues", title: t("help.leaguesTitle"), body: t("help.leaguesBody") },
    { id: "tipping", title: t("help.tippingTitle"), body: t("help.tippingBody") },
    { id: "bulk", title: t("help.bulkTitle"), body: t("help.bulkBody") },
    { id: "rules", title: t("help.rulesTitle"), body: t("help.rulesBody") },
    { id: "leagueSettings", title: t("help.leagueSettingsTitle"), body: t("help.leagueSettingsBody") },
    { id: "groupTips", title: t("help.groupTipsTitle"), body: t("help.groupTipsBody") },
    { id: "table", title: t("help.tableTitle"), body: t("help.tableBody") },
    { id: "push", title: t("help.pushTitle"), body: t("help.pushBody") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={`${t("help.title")} — Tipomatic`}
        description={t("help.metaDesc")}
        path="/help"
      />

      <header className="sticky top-0 z-40 bg-gradient-hero text-white shadow-elevated">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-bold text-white">
            <img
              src="/icon-512.png?v=2"
              alt="Tipomatic"
              className="h-8 w-8 rounded-lg object-cover"
            />
            Tipomatic
          </div>
          <Button
            asChild
            variant="ghost"
            className="text-white hover:bg-white/15 hover:text-white"
          >
            <Link to="/">
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t("legal.back")}
            </Link>
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-2xl">
        <h1 className="text-3xl font-bold mb-2">{t("help.title")}</h1>
        <p className="text-muted-foreground mb-8">{t("help.subtitle")}</p>

        <Accordion type="single" collapsible className="space-y-3">
          <AccordionItem
            value="install"
            className="rounded-xl border-2 border-primary/40 bg-primary/[0.04] px-5 shadow-card"
          >
            <AccordionTrigger headingLevel="h2" className="text-left text-base font-semibold hover:no-underline py-4 text-primary">
              ⭐ {t("help.installTitle")}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 space-y-4">
              <p>{t("help.installBody")}</p>
              <video
                src={installVideo.url}
                controls
                playsInline
                muted
                className="w-full rounded-lg border bg-black"
              />

              {installed ? (
                <div className="rounded-lg border border-primary/30 bg-primary/[0.06] p-3 text-foreground">
                  ✅ {t("help.installAlreadyInstalled")}
                </div>
              ) : (
                <>
                  {platform !== "ios" && (
                    <Button
                      onClick={handleInstall}
                      disabled={!canPrompt}
                      className="w-full sm:w-auto"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t("help.installBtn")}
                    </Button>
                  )}

                  {showFallback && (
                    <div className="space-y-3">
                      {platform !== "ios" && (
                        <p className="text-xs italic">{t("help.installFallbackHint")}</p>
                      )}
                      {(platform === "ios" || platform === "other") && (
                        <div className="rounded-lg border bg-card p-3 text-foreground">
                          <p className="font-semibold mb-1">{t("help.installIosTitle")}</p>
                          <p className="whitespace-pre-line text-muted-foreground">
                            {t("help.installIosSteps")}
                          </p>
                        </div>
                      )}
                      {(platform === "android" || platform === "other") && (
                        <div className="rounded-lg border bg-card p-3 text-foreground">
                          <p className="font-semibold mb-1">{t("help.installAndroidTitle")}</p>
                          <p className="whitespace-pre-line text-muted-foreground">
                            {t("help.installAndroidSteps")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </AccordionContent>
          </AccordionItem>

          {sections.map((s) => (
            <AccordionItem
              key={s.id}
              value={s.id}
              className="rounded-xl border bg-card px-5 shadow-card"
            >
              <AccordionTrigger headingLevel="h2" className="text-left text-base font-semibold hover:no-underline py-4">
                {s.title}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4 space-y-3">
                {s.body.split('\n\n').map((paragraph, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-8 rounded-xl border bg-card px-5 py-6 shadow-card text-center space-y-3">
          <p className="text-sm text-muted-foreground">{t("help.bmcBody")}</p>
          <a
            href="https://www.buymeacoffee.com/incubator"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFDD00] text-black px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition"
          >
            <Coffee className="h-4 w-4" />
            {t("help.bmcButton")}
          </a>
        </div>
      </main>
    </div>
  );
}
